/**
 * <guide-quiz> (sections/guide-quiz.liquid).
 *
 * The section ships every answer as a real <a> to the collection filtered by
 * that one answer, so the page works with no JavaScript at all. This upgrades
 * it into a live quiz: answers become selectable, the choices from every
 * question combine, and the matching products appear on the page as you go.
 *
 * THE URL
 *
 * Three kinds of answer, told apart by data-kind, and each goes in the part
 * of the URL where Shopify combines it correctly:
 *
 *   tag    -> the PATH:   /collections/mice/grip-claw+hand-small
 *   price  -> the query:  ?filter.v.price.gte=5000&filter.v.price.lte=10000
 *   meta   -> the query:  ?filter.p.m.custom.filter_size=Cloth
 *
 * Tags in the path AND together. Tags in the query (`filter.p.tag=a&filter.p
 * .tag=b`) OR together - Shopify ORs repeated values of one filter - which is
 * why this file used to make every second answer WIDEN the result. Different
 * query filters AND with each other and with the path, so a tag question, a
 * price question and a metafield question narrow as expected.
 *
 * LIVE RESULTS
 *
 * Every change rebuilds that URL and, after 300ms of quiet, fetches it with
 * `section_id=guide-results`. sections/guide-results.liquid renders the
 * theme's own product grid for that filtered collection and carries the
 * total in data-results-count. The count line and the grid are replaced
 * from the response; the button's href is the same URL without the
 * section_id, so "see all" lands on the collection page showing the same
 * products.
 *
 * A response that arrives after a newer request was sent is dropped - the
 * sequence number guards against a slow first answer overwriting a fast
 * second one.
 *
 * Selection is per QUESTION: a second answer in the same question replaces
 * the first (they are alternatives); answers in different questions
 * accumulate (they are constraints). Clicking a chosen answer again clears
 * it.
 */
(function () {
  if (customElements.get('guide-quiz')) return;

  var RESULTS_SECTION = 'guide-results';
  var DEBOUNCE_MS = 300;

  class GuideQuiz extends HTMLElement {
    connectedCallback() {
      this.baseUrl = this.dataset.baseUrl || '/collections/all';
      this.baseQuery = this.dataset.baseQuery || '';
      this.total = parseInt(this.dataset.total, 10) || 0;
      this.countTemplate = this.dataset.countTemplate || '%count%';
      this.noneTemplate = this.dataset.noneTemplate || '';
      this.seeAllTemplate = this.dataset.seeAllTemplate || '';

      this.result = this.querySelector('[data-result]');
      this.cta = this.querySelector('[data-cta]');
      this.countEl = this.querySelector('[data-count]');
      this.grid = this.querySelector('[data-grid]');
      this.noneEl = this.querySelector('[data-none]');
      this.soldOutTemplate = this.dataset.soldOutTemplate || '';
      this.soldOutNoteTemplate = this.dataset.soldOutNoteTemplate || '';
      this.resetButton = this.querySelector('[data-reset]');
      if (!this.result || !this.cta) return;

      this.chosen = new Map();
      /* Typed budgets, keyed by question like chosen answers are. */
      this.typed = new Map();
      this.timer = null;
      this.sequence = 0;
      this.ctaLabel = this.cta.textContent.trim();

      this.onClick = this.onClick.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onReset = this.onReset.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('input', this.onInput);
      if (this.resetButton) this.resetButton.addEventListener('click', this.onReset);

      this.result.hidden = false;

      /* An answer can arrive in the URL (?answer=grip-claw) from a link on
         another page - the home page's guide entry cards hand the visitor in
         with their first answer already chosen. Unknown values are ignored. */
      var self = this;
      try {
        new URLSearchParams(window.location.search).getAll('answer').forEach(function (value) {
          var el = self.querySelector('[data-answer][data-value="' + value.replace(/"/g, '') + '"]');
          var question = el && el.closest('[data-question]');
          if (el && question) self.pick(question, el);
        });
      } catch (e) { /* no URL access, no preselect */ }

      this.render();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('input', this.onInput);
      if (this.resetButton) this.resetButton.removeEventListener('click', this.onReset);
      if (this.timer) clearTimeout(this.timer);
    }

    onClick(event) {
      var answer = event.target.closest('[data-answer]');
      if (!answer || !this.contains(answer)) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey) return;
      event.preventDefault();

      var question = answer.closest('[data-question]');
      if (!question) return;

      this.pick(question, answer);
      /* A chip and a typed amount in the same question are alternatives. */
      if (this.chosen.has(question)) {
        var field = question.querySelector('[data-budget]');
        if (field) field.value = '';
        this.typed.delete(question);
      }
      this.render();
    }

    /* A filter question (a product metafield filter) takes several answers:
       Shopify ORs values of the same filter, so "Claw" and "Palm" together
       mean either grip - the list widens, as a visitor expects. Tag and price
       questions stay single-choice: tags in the path AND together, and two
       price ranges cannot both apply. Clicking a chosen answer clears it. */
    isMulti(answer) {
      var kind = answer.getAttribute('data-kind') || 'tag';
      return kind !== 'tag' && kind !== 'price' && !!answer.getAttribute('data-param');
    }

    pick(question, answer) {
      var list = (this.chosen.get(question) || []).slice();
      var at = list.indexOf(answer);
      if (at !== -1) {
        list.splice(at, 1);
      } else if (this.isMulti(answer)) {
        list.push(answer);
      } else {
        list = [answer];
      }
      if (list.length) this.chosen.set(question, list);
      else this.chosen.delete(question);
    }

    onInput(event) {
      var field = event.target.closest('[data-budget]');
      if (!field || !this.contains(field)) return;
      var question = field.closest('[data-question]');
      if (!question) return;
      var value = parseInt(field.value, 10);
      if (value > 0) {
        this.typed.set(question, { param: field.getAttribute('data-param') || 'filter.v.price', max: value });
        this.chosen.delete(question);
      } else {
        this.typed.delete(question);
      }
      this.render();
    }

    onReset() {
      this.chosen.clear();
      this.typed.clear();
      this.querySelectorAll('[data-budget]').forEach(function (field) {
        field.value = '';
      });
      this.render();
    }

    /* Build the collection URL from the chosen answers. withStock=false
       leaves the in-stock filter off: the same answers, sold out included. */
    buildUrl(withStock) {
      var tags = [];
      var query = [];
      var answers = [];
      this.chosen.forEach(function (list) {
        answers = answers.concat(list);
      });
      answers.forEach(function (answer) {
        var kind = answer.getAttribute('data-kind') || 'tag';
        var value = answer.getAttribute('data-value') || '';
        var param = answer.getAttribute('data-param') || '';
        /* "any" is the value for answers like "I don't mind": the question is
           answered but adds no filter. A real value there ("Standard") left
           one mouse in the results, and none once other answers joined. */
        if (!value || value.toLowerCase() === 'any') return;
        if (kind === 'tag') {
          if (tags.indexOf(value) === -1) tags.push(value);
        } else if (kind === 'price') {
          var bounds = value.split('-');
          if (bounds[0]) query.push(param + '.gte=' + encodeURIComponent(bounds[0]));
          if (bounds[1]) query.push(param + '.lte=' + encodeURIComponent(bounds[1]));
        } else if (param) {
          /* The parameter name is not encoded: it contains dots, which are
             legal unencoded and which Shopify expects to see literally. */
          query.push(param + '=' + encodeURIComponent(value));
        }
      });
      this.typed.forEach(function (budget) {
        query.push(budget.param + '.lte=' + encodeURIComponent(budget.max));
      });
      if (this.baseQuery && withStock !== false) query.push(this.baseQuery);
      var url = this.baseUrl.replace(/\/$/, '');
      if (tags.length) url += '/' + tags.map(encodeURIComponent).join('+');
      if (query.length) url += '?' + query.join('&');
      return url;
    }

    render() {
      var self = this;
      this.querySelectorAll('[data-answer]').forEach(function (el) {
        var isChosen = (self.chosen.get(el.closest('[data-question]')) || []).indexOf(el) !== -1;
        el.classList.toggle('is-chosen', isChosen);
        el.setAttribute('aria-pressed', isChosen ? 'true' : 'false');
      });

      var answered = this.chosen.size + this.typed.size;
      var url = this.buildUrl();
      this.cta.setAttribute('href', url);
      if (this.resetButton) this.resetButton.hidden = answered === 0;

      if (answered === 0) {
        /* Nothing chosen: the grid is left empty rather than filled with the
           first page of everything. The total comes from Liquid, except when
           the quiz is in-stock only - products_count does not know about
           stock - in which case it is fetched once and remembered. */
        if (this.timer) clearTimeout(this.timer);
        this.sequence++;
        this.setGrid('');
        this.setNone(false);
        if (this.baseQuery && this.stockTotal === undefined) {
          this.fetchTotal(url);
        } else {
          var total = this.stockTotal !== undefined ? this.stockTotal : this.total;
          this.showCount(total);
          this.setCta(total);
        }
        return;
      }

      if (this.timer) clearTimeout(this.timer);
      this.timer = setTimeout(function () {
        self.fetchResults(url);
      }, DEBOUNCE_MS);
    }

    fetchResults(url) {
      var self = this;
      var seq = ++this.sequence;
      var separator = url.indexOf('?') === -1 ? '?' : '&';
      if (this.grid) this.grid.setAttribute('aria-busy', 'true');

      fetch(url + separator + 'section_id=' + RESULTS_SECTION, {
        headers: { Accept: 'text/html' }
      })
        .then(function (response) {
          return response.ok ? response.text() : '';
        })
        .then(function (html) {
          if (seq !== self.sequence) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var wrap = doc.querySelector('[data-results-count]');
          var count = wrap ? parseInt(wrap.getAttribute('data-results-count'), 10) || 0 : 0;
          var grid = wrap ? wrap.querySelector('.guide-results__grid') : null;

          if (count === 0 && self.baseQuery && self.soldOutTemplate) {
            /* Nothing in stock matches. Before saying so, ask the same
               question without the stock filter: "sold out right now" is a
               different answer from "does not exist", and the visitor can
               ask to be told when it is back from the product page. */
            self.fetchSoldOut(seq);
            return;
          }
          self.showCount(count);
          self.setGrid(grid ? grid.outerHTML : '');
          self.setNone(count === 0);
          self.setCta(count);
        })
        .catch(function () {
          if (seq !== self.sequence) return;
          /* The button still works; the visitor loses only the preview. */
          self.setGrid('');
          self.setNone(false);
        })
        .finally(function () {
          if (seq === self.sequence && self.grid) self.grid.setAttribute('aria-busy', 'false');
        });
    }

    /* The fallback when the in-stock query is empty: the same answers with
       the stock filter off. Products that match but are sold out are shown
       with their sold-out state, under a line that says so; the button then
       links to that wider list. */
    fetchSoldOut(seq) {
      var self = this;
      var url = this.buildUrl(false);
      var separator = url.indexOf('?') === -1 ? '?' : '&';
      fetch(url + separator + 'section_id=' + RESULTS_SECTION, { headers: { Accept: 'text/html' } })
        .then(function (response) { return response.ok ? response.text() : ''; })
        .then(function (html) {
          if (seq !== self.sequence) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var wrap = doc.querySelector('[data-results-count]');
          var count = wrap ? parseInt(wrap.getAttribute('data-results-count'), 10) || 0 : 0;
          var grid = wrap ? wrap.querySelector('.guide-results__grid') : null;
          if (count > 0) {
            self.showCount(count, self.soldOutTemplate);
            self.setGrid(grid ? grid.outerHTML : '');
            self.setNone(true, self.soldOutNoteTemplate);
            self.cta.setAttribute('href', url);
            self.setCta(count);
          } else {
            self.showCount(0);
            self.setGrid('');
            self.setNone(true);
            self.setCta(0);
          }
        })
        .catch(function () {
          if (seq !== self.sequence) return;
          self.showCount(0);
          self.setGrid('');
          self.setNone(true);
          self.setCta(0);
        })
        .finally(function () {
          if (seq === self.sequence && self.grid) self.grid.setAttribute('aria-busy', 'false');
        });
    }

    /* One request, on load, for the in-stock total; the answer is kept so
       clearing the quiz never asks again. */
    fetchTotal(url) {
      var self = this;
      var separator = url.indexOf('?') === -1 ? '?' : '&';
      fetch(url + separator + 'section_id=' + RESULTS_SECTION, { headers: { Accept: 'text/html' } })
        .then(function (response) { return response.ok ? response.text() : ''; })
        .then(function (html) {
          var m = /data-results-count="(\d+)"/.exec(html);
          self.stockTotal = m ? parseInt(m[1], 10) : self.total;
          if (self.chosen.size + self.typed.size === 0) {
            self.showCount(self.stockTotal);
            self.setCta(self.stockTotal);
          }
        })
        .catch(function () {
          self.stockTotal = self.total;
          if (self.chosen.size + self.typed.size === 0) {
            self.showCount(self.total);
            self.setCta(self.total);
          }
        });
    }

    showCount(count, template) {
      if (!this.countEl) return;
      this.countEl.textContent = (template || this.countTemplate).replace('%count%', String(count));
    }

    setGrid(html) {
      if (!this.grid) return;
      this.grid.innerHTML = html;
      /* Every product card carries [data-reveal], which base.css hides until
         assets/reveal.js marks it revealed. Nothing observes nodes that
         arrived by innerHTML, so they are handed to the reveal pass the same
         way facets.js does after a filter; if reveal.js has not parsed yet
         the initial pass will catch them. */
      if (window.ThemeReveal) window.ThemeReveal.init(this.grid);
    }

    setNone(show, text) {
      if (!this.noneEl) return;
      this.noneEl.hidden = !show;
      if (show) this.noneEl.textContent = text || this.noneTemplate;
    }

    setCta(count) {
      if (this.chosen.size + this.typed.size === 0 || !this.seeAllTemplate) {
        this.cta.textContent = this.ctaLabel;
      } else {
        this.cta.textContent = this.seeAllTemplate.replace('%count%', String(count));
      }
      this.cta.classList.toggle('is-disabled', count === 0);
      this.cta.setAttribute('aria-disabled', count === 0 ? 'true' : 'false');
    }
  }

  customElements.define('guide-quiz', GuideQuiz);
})();

